import React, {useMemo, useState} from "react";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import {Paper} from "@mui/material";
import {ProductTypes} from "../../../../../../../../../core/constants/enums";
import {Row} from "reactstrap";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import Chips, {ChipsIconPosition, ChipsTypes} from "../../../../../../../../components/base/chips/chips";
import SerialBarcodeDialog from "../../../../../../../../components/dialogs/serial-barcode";
import {CreditInformationViewProductsDataGridColumnNames} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import ProductInformationPopover from "../../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import EnvService from "../../../../../../../../../core/services/env";


const dataGridSaveConfig = {
    name: 'CreditInformationViewProductsSectionDataGrid',
    version: 3,
}

// TODO: add warehouse column to products of credit information view.

const CreditInformationViewProductsSection = ({
                                                  order,
                                                  orderLoading,
                                              }) => {

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Part No",
                name: CreditInformationViewProductsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
            },
            {
                title: "Warehouse",
                name: CreditInformationViewProductsDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
            },
            {
                title: "Description",
                name: CreditInformationViewProductsDataGridColumnNames.description,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 250,
            },
            {
                title: "Discount",
                name: CreditInformationViewProductsDataGridColumnNames.discount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Quantity",
                name: CreditInformationViewProductsDataGridColumnNames.quantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Unit Price",
                name: CreditInformationViewProductsDataGridColumnNames.unitPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Total Price",
                name: CreditInformationViewProductsDataGridColumnNames.totalPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: "Serials / Committed Quantities",
                name: CreditInformationViewProductsDataGridColumnNames.committedQuantity,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 350,
            },
            ...(EnvService.DEV_showOrderProductComments
                ? [{
                    title: "Comment",
                    name: CreditInformationViewProductsDataGridColumnNames.comment,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 220,
                }]
                : []),
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return order?.items?.map(item => {
            return {
                key: item.id,
                data: item,
                cells: {
                    [CreditInformationViewProductsDataGridColumnNames.warehouse]: item?.whse,
                    [CreditInformationViewProductsDataGridColumnNames.partNo]:
                        <ProductInformationPopover partNo={order?.appliedGiftCardInfo?.equalPartNo === item?.partNo ? null : item?.partNo}>
                            {item?.partNo ?? ""}
                        </ProductInformationPopover>,
                    [CreditInformationViewProductsDataGridColumnNames.description]: item?.description,
                    [CreditInformationViewProductsDataGridColumnNames.discount]: `${item.discountPct ?? '0'} %`,
                    [CreditInformationViewProductsDataGridColumnNames.quantity]: item.orderQty,
                    [CreditInformationViewProductsDataGridColumnNames.unitPrice]: item.unitPriceBeforeDiscount ?? 0,
                    [CreditInformationViewProductsDataGridColumnNames.totalPrice]: (Number(item.unitPrice ?? '0') * (item.orderQty ?? 0)),
                    [CreditInformationViewProductsDataGridColumnNames.committedQuantity]:
                        !item?.typeInfo?.code || item?.typeInfo?.code === ProductTypes.nonPhysical
                            ? item?.typeInfo?.name ?? '--'
                            : item.isSerialized
                                ? <SerialsContainer serials={item?.serials ?? []}/>
                                : item?.committedQty,
                    ...(EnvService.DEV_showOrderProductComments && {
                        [CreditInformationViewProductsDataGridColumnNames.comment]:
                            item?.comment?.trim() ? item.comment : '--',
                    }),
                },
            }
        })
    }, [order?.appliedGiftCardInfo?.equalPartNo, order?.items])

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center'>
                <Paper className='paper w-100 px-3 py-2'>
                    <Row className='no-gutters align-items-center justify-content-between w-100'>
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            columns={dataGridColumns}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            hideFooter
                            hideExporting
                            rows={dataGridRows}
                            visibleRows={10}
                            state={{
                                loading: {state: orderLoading},
                                density: dataGridDensity,
                            }}
                        />
                        <div className='my-2 w-100'/>
                    </Row>
                </Paper>
            </Row>
        </>
    )
}

const SerialsContainer = ({serials}) => {
    const [selectedSerial, setSelectedSerial] = useState();

    const chips = useMemo(() => {
        return serials?.map(serialNumber =>
            <span key={`${serialNumber.id}`} {...dataGridCellTextProps} className={'mr-2 mb-2'}>
            <Chips
                type={ChipsTypes.next}
                iconPosition={ChipsIconPosition.end}
                content={serialNumber.serialNumber}
                onIconClick={() => setSelectedSerial(serialNumber)}
            />
            </span>
        )
    }, [serials])

    return (
        <>
            <div className={'d-flex w-100 flex-wrap'}>
                {chips}
            </div>
            <SerialBarcodeDialog
                open={!!selectedSerial}
                setOpen={() => setSelectedSerial(undefined)}
                serial={selectedSerial}
            />
        </>
    );
}

export default CreditInformationViewProductsSection;
